import React from 'react';
import styled from 'styled-components';
import { getToneBackground, getToneForeground } from '../../theme/tone';
import type { DocumentTaxonomy } from '../../types';

/** Pill chip rendering a document's taxonomy display label (e.g. "Lab Reports").
 * Used in the table category column, the list/compact row, and the
 * document-inspector header so they stay consistent. The label + ordering are
 * server-derived (DocumentTaxonomy).
 *
 * The taxonomy is currently FLAT, so the label is a single tier and renders on
 * one line with no arrow. The tier-stacking layout below is retained (it
 * degrades cleanly to one line) so that re-introducing leaves — "Section → Leaf"
 * with a leading arrow on child tiers — needs no chip changes. */
const Chip = styled.span<{ $tone: string; $maxWidth: string }>`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.25;
    background: ${({ $tone }) => getToneBackground($tone)};
    color: ${({ $tone }) => getToneForeground($tone)};
    /* Cap the chip + never let it grow to steal the title's space. Each tier
       line ellipsizes to the full label, which lives in the title attribute. */
    max-width: ${({ $maxWidth }) => $maxWidth};
    flex-shrink: 0;
`;

/** One tier line. The leading arrow on child tiers is dimmed and never counts
 * against the ellipsis of the label text. */
const TierLine = styled.span`
    display: flex;
    align-items: baseline;
    gap: 2px;
    max-width: 100%;
    min-width: 0;
`;

const TierArrow = styled.span`
    flex-shrink: 0;
    opacity: 0.55;
`;

const TierText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const Dash = styled.span`
    color: #bbb;
    font-size: 12px;
`;

/** Split the server-derived display label into its tiers. The separator (" → ")
 * is a stable contract from taxonomy.display_label — we keep ALL label
 * derivation server-side and only break on it here. */
const splitTiers = (label: string): string[] =>
    label.split('→').map(part => part.trim()).filter(Boolean);

export interface TaxonomyChipProps {
    taxonomy?: DocumentTaxonomy | null;
    /** Tone of the chip; 'info' (blue) by default to mark the auto-derived class. */
    tone?: 'default' | 'info' | 'warning' | 'success' | 'danger';
    /** What to render when there's no taxonomy. Default: an em dash. */
    emptyFallback?: React.ReactNode;
    /** Cap on the chip width before each tier line ellipsizes. Default 160px.
     * Pass 'none' (e.g. in the inspector) to show full labels untruncated. */
    maxWidth?: string;
}

export const TaxonomyChip: React.FC<TaxonomyChipProps> = ({
    taxonomy,
    tone = 'info',
    emptyFallback = <Dash>—</Dash>,
    maxWidth = '160px',
}) => {
    if (!taxonomy || !taxonomy.displayLabel) {
        return <>{emptyFallback}</>;
    }
    // The classifier gave nothing for a doc that had OCR text — the label is a
    // catch-all sentinel, not a real classification. Badge it for review rather
    // than presenting the sentinel as if it were a confident answer.
    if (taxonomy.classificationFailed) {
        return (
            <Chip
                $tone="warning"
                $maxWidth={maxWidth}
                title="Automatic classification failed for this document — it needs manual review."
            >
                <TierLine>
                    <TierText>Needs review — not classified</TierText>
                </TierLine>
            </Chip>
        );
    }
    const tiers = splitTiers(taxonomy.displayLabel);
    const title = taxonomy.certainty
        ? `${taxonomy.displayLabel} · model certainty: ${taxonomy.certainty} ` +
          `(how committed the model was — not a probability of being correct)`
        : taxonomy.displayLabel;
    return (
        <Chip $tone={tone} $maxWidth={maxWidth} title={title}>
            {tiers.map((tier, i) => (
                <TierLine key={`${i}-${tier}`}>
                    {i > 0 && <TierArrow aria-hidden>→</TierArrow>}
                    <TierText>{tier}</TierText>
                </TierLine>
            ))}
        </Chip>
    );
};
